import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, Lock, KeyRound, Store, SlidersHorizontal, Pencil, Star, Tag, Trash2, ChevronUp, ChevronDown, Check, X, ShoppingBag } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import {
  partnerGetMenu, partnerCreateCategory, partnerCreateItem,
  partnerUpdateItem, partnerUpdateCategory, partnerSetStock,
  partnerAccountMe, partnerChangePassword,
  partnerDeleteCategory, partnerDeleteItem, partnerGetCatalogs,
  partnerReorderCategories, partnerReorderItems, partnerSetItemCategories,
} from "../lib/api";
import { PartnerOptionsEditor } from "../components/PartnerOptionsEditor";
import { PartnerItemEditor } from "../components/PartnerItemEditor";
import { ImageUpload } from "../components/ImageUpload";

const GREEN = "#075A43";
const ORANGE = "#FF5A1F";
const GOLD = "#C9A227";
const field = { background: "#f5f7f6", border: "1px solid #e5e7eb" };

const KINDS = [
  ["", "Type…"], ["entree", "Entrée"], ["plat", "Plat"], ["dessert", "Dessert"],
  ["menu", "Menu"], ["boisson", "Boisson"], ["formule", "Formule"], ["accompagnement", "Accompagnement"],
];

export default function PartnerManage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const adminRid = searchParams.get("restaurant_id");
  const user = useAuthStore((s) => s.user);
  const isFounder = ["admin", "manager"].includes(user?.role);
  // Founder (admin/manager) accède au MÊME cockpit pour gérer le catalogue La BASE Maison.
  const isPartner = user?.role === "restaurant" || user?.role === "admin" || user?.role === "manager";

  const [account, setAccount] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newCat, setNewCat] = useState({ name: "", kind: "" });
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(false);
  const [optItem, setOptItem] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editFocusPromo, setEditFocusPromo] = useState(false);
  const [catalogs, setCatalogs] = useState([]);
  const [cubeId, setCubeId] = useState(null);
  const [catEdit, setCatEdit] = useState(null); // { id, name } renommage sous-rubrique

  // Charge la liste des catalogues (1 par cube pour le Founder) une seule fois.
  useEffect(() => {
    if (!isPartner) { setLoading(false); return; }
    // Founder en supervision d'un partenaire précis (depuis « Partenaires » → Tableau de bord).
    if (adminRid && isFounder) { setCatalogs([]); setCubeId(adminRid); return; }
    partnerGetCatalogs()
      .then((res) => {
        const list = res?.catalogs || [];
        setCatalogs(list);
        setCubeId(list.length ? list[0].id : null);
        if (!list.length) setLoading(false);
      })
      .catch(() => { setCatalogs([]); setCubeId(null); setLoading(false); });
  }, [isPartner, adminRid, isFounder]);

  const load = useCallback(() => {
    if (!cubeId) return;
    setLoading(true);
    setError(null);
    Promise.all([partnerAccountMe().catch(() => null), partnerGetMenu(cubeId)])
      .then(([acc, menu]) => { setAccount(acc); setData(menu); })
      .catch((e) => setError(e?.response?.status === 403
        ? "Accès réservé : ce compte n'est pas rattaché à un catalogue."
        : "Impossible de charger votre carte."))
      .finally(() => setLoading(false));
  }, [cubeId]);

  useEffect(() => { if (isPartner && cubeId) load(); }, [isPartner, cubeId, load]);

  const draftFor = (cid) => drafts[cid] || { name: "", price: "", subtitle: "", size_label: "", description: "", image_url: "", offerts: "", client_keywords: "" };
  const setDraft = (cid, patch) => setDrafts((d) => ({ ...d, [cid]: { ...draftFor(cid), ...patch } }));

  const addCategory = async () => {
    if (!newCat.name.trim() || busy) return;
    setBusy(true);
    try { await partnerCreateCategory(newCat.name.trim(), newCat.kind || null, cubeId); setNewCat({ name: "", kind: "" }); load(); toast.success("Sous-rubrique ajoutée"); }
    catch { toast.error("Échec"); } finally { setBusy(false); }
  };

  const addItem = async (cid) => {
    const df = draftFor(cid);
    const price = parseFloat(String(df.price).replace(",", "."));
    if (!df.name.trim() || isNaN(price) || price < 0 || busy) return;
    setBusy(true);
    try {
      await partnerCreateItem({
        category_id: cid, name: df.name.trim(), price,
        subtitle: df.subtitle.trim() || null, size_label: df.size_label.trim() || null,
        description: df.description.trim() || null, image_url: df.image_url.trim() || null,
        offerts: df.offerts.trim() ? df.offerts.split(",").map((s) => ({ label: s.trim() })).filter((o) => o.label) : [],
        client_keywords: df.client_keywords.trim() ? df.client_keywords.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      setDraft(cid, { name: "", price: "", subtitle: "", size_label: "", description: "", image_url: "", offerts: "", client_keywords: "" });
      load(); toast.success("Produit ajouté");
    } catch { toast.error("Échec"); } finally { setBusy(false); }
  };

  const toggleItem = async (item) => {
    setBusy(true);
    try { await partnerSetStock(item.id, { is_available: !(item.is_available !== false) }); load(); }
    finally { setBusy(false); }
  };
  const toggleItemActive = async (item) => {
    setBusy(true);
    try { await partnerUpdateItem(item.id, { is_active: !(item.is_active !== false) }); load(); }
    finally { setBusy(false); }
  };
  const setTrack = async (item, track) => {
    setBusy(true);
    try { await partnerSetStock(item.id, { track_stock: track }); load(); }
    finally { setBusy(false); }
  };
  const adjustStock = async (item, delta) => {
    setBusy(true);
    try { await partnerSetStock(item.id, { delta }); load(); }
    finally { setBusy(false); }
  };
  const saveOptions = async (option_groups) => {
    try {
      await partnerUpdateItem(optItem.id, { option_groups });
      toast.success("Options enregistrées"); setOptItem(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec des options"); }
  };
  const saveItem = async (patch) => {
    try {
      await partnerUpdateItem(editItem.id, patch);
      toast.success("Article mis à jour"); setEditItem(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Échec de la mise à jour"));
    }
  };
  const openEditItem = (item) => { setEditFocusPromo(false); setEditItem(item); };
  const openPromo = (item) => { setEditFocusPromo(true); setEditItem(item); };
  const toggleCat = async (cat) => {
    setBusy(true);
    try { await partnerUpdateCategory(cat.id, { is_active: !(cat.is_active !== false) }); load(); }
    finally { setBusy(false); }
  };
  const renameCat = async () => {
    if (!catEdit?.name.trim() || busy) return;
    setBusy(true);
    try { await partnerUpdateCategory(catEdit.id, { name: catEdit.name.trim() }); setCatEdit(null); load(); toast.success("Sous-rubrique renommée"); }
    catch { toast.error("Échec du renommage"); } finally { setBusy(false); }
  };
  const saveCatPhoto = async (cat, image_url) => {
    setBusy(true);
    try { await partnerUpdateCategory(cat.id, { image_url: image_url || "" }); load(); }
    catch { toast.error("Échec de la photo"); } finally { setBusy(false); }
  };
  const moveCat = async (index, dir) => {
    const cats = data?.categories || [];
    const j = index + dir;
    if (j < 0 || j >= cats.length || busy) return;
    const ids = cats.map((c) => c.id);
    [ids[index], ids[j]] = [ids[j], ids[index]];
    setBusy(true);
    try { await partnerReorderCategories(ids, cubeId); load(); }
    catch { toast.error("Échec du réordonnancement"); } finally { setBusy(false); }
  };
  const moveItem = async (cat, index, dir) => {
    const items = cat.items || [];
    const j = index + dir;
    if (j < 0 || j >= items.length || busy) return;
    const ids = items.map((i) => i.id);
    [ids[index], ids[j]] = [ids[j], ids[index]];
    setBusy(true);
    try { await partnerReorderItems(cat.id, ids); load(); }
    catch { toast.error("Échec du réordonnancement"); } finally { setBusy(false); }
  };
  const assignItemCats = async (itemId, categoryIds) => {
    await partnerSetItemCategories(itemId, categoryIds);
  };
  const deleteCat = async (cat) => {
    const n = cat.item_count ?? (cat.items || []).length;
    if (!window.confirm(`Supprimer la sous-rubrique « ${cat.name} »${n ? ` et ses ${n} produit(s)` : ""} ? Cette action est définitive.`)) return;
    setBusy(true);
    try { await partnerDeleteCategory(cat.id); toast.success("Sous-rubrique supprimée"); load(); }
    catch { toast.error("Échec de la suppression"); } finally { setBusy(false); }
  };
  const deleteItem = async (item) => {
    if (!window.confirm(`Supprimer « ${item.name} » ? Cette action est définitive.`)) return;
    setBusy(true);
    try { await partnerDeleteItem(item.id); toast.success("Produit supprimé"); load(); }
    catch { toast.error("Échec de la suppression"); } finally { setBusy(false); }
  };

  if (!isPartner) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="pm-access-denied">
        <Lock size={44} color="#cbd5e1" className="mx-auto mb-4" />
        <p style={{ color: "#64748b" }}>Espace réservé aux restaurants partenaires. Connectez-vous avec votre compte partenaire.</p>
        <button data-testid="pm-go-login" onClick={() => nav("/login")} className="mt-5 rounded-xl font-bold text-white px-6 py-3" style={{ background: GREEN }}>Se connecter</button>
      </div>
    );
  }

  if (!loading && account?.must_change_password) {
    return <ForcePassword onDone={load} />;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="pm-page">
      {loading ? (
        <div className="h-40 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} data-testid="pm-loading" />
      ) : error ? (
        <div className="text-center py-10">
          <p style={{ color: "#64748b" }}>{error}</p>
          <button data-testid="pm-retry" onClick={load} className="mt-4 rounded-xl font-bold text-white px-5 py-2" style={{ background: GREEN }}>Réessayer</button>
        </div>
      ) : (
        <>
          {adminRid && isFounder && (
            <button data-testid="pm-back-to-partners" onClick={() => nav("/founder/partenaires")}
              className="mb-4 rounded-xl font-bold px-4 py-2 text-sm" style={{ background: "#eef6f2", color: GREEN, border: `1px solid ${GREEN}` }}>
              ← Retour aux partenaires
            </button>
          )}
          <div className="flex items-center gap-3 mb-1">
            <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: GREEN }}><Store size={22} color="#fff" /></div>
            <div>
              <h1 className="text-3xl font-extrabold leading-tight" style={{ color: GREEN }}>{data?.restaurant?.name}</h1>
              {data?.restaurant?.tier && <span className="text-xs font-extrabold px-2 py-0.5 rounded-full" style={{ background: data.restaurant.tier === "gold" ? "#fdf6e3" : "#eafff6", color: data.restaurant.tier === "gold" ? GOLD : GREEN, border: `1px solid ${data.restaurant.tier === "gold" ? GOLD : GREEN}` }}>{data.restaurant.tier === "gold" ? "GOLD" : "PREMIUM"}</span>}
            </div>
          </div>
          <p className="text-sm mt-1 mb-6" style={{ color: "#64748b" }}>Gérez votre carte. Les changements sont visibles immédiatement côté client.</p>

          {/* Sélecteur de cube (Founder : 1 catalogue par univers) */}
          {catalogs.length > 1 && (
            <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="pm-cube-tabs">
              {catalogs.map((c) => {
                const active = c.id === cubeId;
                return (
                  <button key={c.id} data-testid={`pm-cube-${c.id}`} onClick={() => setCubeId(c.id)}
                    className="rounded-full px-4 h-10 text-sm font-bold whitespace-nowrap shrink-0"
                    style={active ? { background: GREEN, color: "#fff" } : { background: "#f5f7f6", color: GREEN, border: "1px solid #e5e7eb" }}>
                    {(c.name.split("—").pop() || c.name).trim()}
                  </button>
                );
              })}
            </div>
          )}

          {/* Ajouter une sous-rubrique */}
          <div className="flex gap-2 mb-8">
            <input data-testid="pm-new-category-input" value={newCat.name} onChange={(e) => setNewCat((c) => ({ ...c, name: e.target.value }))}
              placeholder="Nouvelle sous-rubrique (ex : Entrées)"
              className="flex-1 rounded-xl px-4 h-11 text-sm outline-none" style={field} />
            <select data-testid="pm-new-category-kind" value={newCat.kind} onChange={(e) => setNewCat((c) => ({ ...c, kind: e.target.value }))}
              className="rounded-xl px-3 h-11 text-sm outline-none" style={field}>
              {KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button data-testid="pm-add-category" onClick={addCategory} disabled={busy}
              className="w-11 h-11 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: GREEN }}>
              <Plus size={22} />
            </button>
          </div>

          {(data?.categories || []).length === 0 && <p className="text-center" style={{ color: "#64748b" }}>Aucune sous-rubrique. Ajoutez-en une pour commencer.</p>}

          {(data?.categories || []).map((cat, catIndex) => {
            const df = draftFor(cat.id);
            const count = cat.item_count ?? (cat.items || []).length;
            const quota = Number.isInteger(cat.max_items) ? cat.max_items : null;
            const full = quota !== null && count >= quota;
            return (
              <div key={cat.id} data-testid={`pm-cat-${cat.id}`} className="mb-6 rounded-2xl p-4" style={{ border: "1px solid #f0f0f0" }}>
                <div className="flex items-center justify-between mb-3">
                  {catEdit?.id === cat.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <input data-testid={`pm-cat-rename-input-${cat.id}`} value={catEdit.name} autoFocus
                        onChange={(e) => setCatEdit((c) => ({ ...c, name: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && renameCat()}
                        className="flex-1 rounded-lg px-3 h-9 text-sm outline-none" style={field} />
                      <button data-testid={`pm-cat-rename-save-${cat.id}`} onClick={renameCat} disabled={busy}
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: GREEN }}><Check size={15} /></button>
                      <button data-testid={`pm-cat-rename-cancel-${cat.id}`} onClick={() => setCatEdit(null)}
                        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#f1f5f9" }}><X size={15} /></button>
                    </div>
                  ) : (
                    <p className="text-lg font-extrabold flex items-center gap-2" style={{ color: "#1f2937", textDecoration: cat.is_active === false ? "line-through" : "none" }}>
                      {cat.name}
                      <button data-testid={`pm-cat-rename-${cat.id}`} onClick={() => setCatEdit({ id: cat.id, name: cat.name })}
                        className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#f1f5f9", color: GREEN }} title="Renommer"><Pencil size={13} /></button>
                      {cat.kind && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#f1f5f9", color: "#64748b" }}>{cat.kind}</span>}
                      <span data-testid={`pm-cat-quota-${cat.id}`} className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: full ? "#fef2f2" : "#eef6f2", color: full ? "#dc2626" : GREEN }}>
                        {count}{quota !== null ? `/${quota}` : ""} produit{count > 1 ? "s" : ""}
                      </span>
                    </p>
                  )}
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`pm-cat-up-${cat.id}`} onClick={() => moveCat(catIndex, -1)} disabled={busy || catIndex === 0}
                      className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#f1f5f9", color: GREEN, opacity: catIndex === 0 ? 0.4 : 1 }} title="Monter"><ChevronUp size={15} /></button>
                    <button data-testid={`pm-cat-down-${cat.id}`} onClick={() => moveCat(catIndex, 1)} disabled={busy || catIndex === (data?.categories || []).length - 1}
                      className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#f1f5f9", color: GREEN, opacity: catIndex === (data?.categories || []).length - 1 ? 0.4 : 1 }} title="Descendre"><ChevronDown size={15} /></button>
                    <label className="flex items-center gap-2 text-xs" style={{ color: "#64748b" }}>
                      {cat.is_active === false ? "Masquée" : "Visible"}
                      <input type="checkbox" data-testid={`pm-cat-toggle-${cat.id}`} checked={cat.is_active !== false} onChange={() => toggleCat(cat)} />
                    </label>
                    <button data-testid={`pm-cat-delete-${cat.id}`} onClick={() => deleteCat(cat)} disabled={busy}
                      className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#fef2f2", color: "#dc2626" }} title="Supprimer la sous-rubrique">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-3 pb-3" style={{ borderBottom: "1px dashed #eef0ef" }}>
                  <span className="text-xs font-semibold" style={{ color: "#64748b" }}>Photo de la vignette</span>
                  <ImageUpload testid={`pm-cat-photo-${cat.id}`} value={cat.image_url || ""} onChange={(url) => saveCatPhoto(cat, url)} />
                </div>

                {(cat.items || []).map((item, itemIndex) => {
                  const unavailable = item.is_available === false;
                  const hidden = item.is_active === false;
                  return (
                  <div key={item.id} data-testid={`pm-item-${item.id}`} className="py-3 border-t" style={{ borderColor: "#f0f0f0" }}>
                    {/* Niveau 1 : infos + raccourci Promo bien visible */}
                    <div className="flex items-start gap-3">
                      {item.image_url
                        ? <img src={item.image_url} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
                        : <div className="w-14 h-14 rounded-lg shrink-0 flex items-center justify-center" style={{ background: "#f1f5f9" }}><ShoppingBag size={18} color="#cbd5e1" /></div>}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold flex items-center gap-1.5 flex-wrap" style={{ color: "#1f2937", textDecoration: (unavailable || hidden) ? "line-through" : "none", opacity: hidden ? 0.55 : 1 }}>
                          {item.name}
                          {item.recommended && (
                            <span data-testid={`pm-item-reco-${item.id}`} className="inline-flex items-center gap-0.5 text-[10px] font-extrabold px-1.5 py-0.5 rounded-full shrink-0" style={{ background: "#fffdf5", color: GOLD, border: `1px solid ${GOLD}` }} title={item.recommended_note || "Recommandé par le chef"}>
                              <Star size={10} /> Reco
                            </span>
                          )}
                        </p>
                        {item.subtitle && <p className="text-xs truncate" style={{ color: "#94a3b8" }}>{item.subtitle}{item.size_label ? ` · ${item.size_label}` : ""}</p>}
                        <p className="text-xs font-bold flex items-center gap-2 flex-wrap mt-0.5" style={{ color: ORANGE }}>
                          {item.promo_active ? (
                            <span data-testid={`pm-item-promo-${item.id}`} className="inline-flex items-center gap-1">
                              {Number(item.effective_price ?? item.price) < Number(item.original_price ?? item.price) ? (
                                <>
                                  <span style={{ textDecoration: "line-through", color: "#94a3b8" }}>CHF {Number(item.original_price ?? item.price).toFixed(2)}</span>
                                  <span>CHF {Number(item.effective_price).toFixed(2)}</span>
                                </>
                              ) : (
                                <span>CHF {Number(item.original_price ?? item.price).toFixed(2)}</span>
                              )}
                            </span>
                          ) : (
                            <span>CHF {Number(item.original_price ?? item.price).toFixed(2)}</span>
                          )}
                          {item.track_stock && (item.reserved_qty > 0) && <span className="font-semibold" style={{ color: "#94a3b8" }}>· {item.reserved_qty} réservé{item.reserved_qty > 1 ? "s" : ""}</span>}
                          {item.orderable === false && <span className="font-bold" style={{ color: "#dc2626" }}>· indispo</span>}
                        </p>
                      </div>
                      <button data-testid={`pm-item-promo-btn-${item.id}`} onClick={() => openPromo(item)}
                        className="shrink-0 flex items-center gap-1 text-xs font-extrabold px-3 h-9 rounded-full"
                        style={item.promo_active ? { background: ORANGE, color: "#fff" } : { background: "#fff7f3", color: ORANGE, border: `1px solid ${ORANGE}66` }}
                        title="Gérer la promotion de ce produit">
                        <Tag size={13} /> {item.promo_active ? (item.promo_label || "Promo") : "Promo"}
                      </button>
                    </div>

                    {/* Niveau 2 : barre d'actions (anti-chevauchement, défile proprement sur mobile) */}
                    <div className="flex items-center flex-wrap gap-2 mt-2">
                      <button data-testid={`pm-item-up-${item.id}`} onClick={() => moveItem(cat, itemIndex, -1)} disabled={busy || itemIndex === 0}
                        className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#f1f5f9", color: GREEN, opacity: itemIndex === 0 ? 0.4 : 1 }} title="Monter"><ChevronUp size={14} /></button>
                      <button data-testid={`pm-item-down-${item.id}`} onClick={() => moveItem(cat, itemIndex, 1)} disabled={busy || itemIndex === (cat.items || []).length - 1}
                        className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#f1f5f9", color: GREEN, opacity: itemIndex === (cat.items || []).length - 1 ? 0.4 : 1 }} title="Descendre"><ChevronDown size={14} /></button>
                      <button data-testid={`pm-item-edit-${item.id}`} onClick={() => openEditItem(item)}
                        className="flex items-center gap-1 text-xs font-bold px-2.5 h-8 rounded-lg" style={{ background: "#f1f5f9", color: GREEN }}>
                        <Pencil size={13} /> Modifier
                      </button>
                      <button data-testid={`pm-item-options-${item.id}`} onClick={() => setOptItem(item)}
                        className="flex items-center gap-1 text-xs font-bold px-2.5 h-8 rounded-lg" style={{ background: "#f1f5f9", color: GREEN }}>
                        <SlidersHorizontal size={13} /> Options{(item.option_groups || []).length ? ` (${item.option_groups.length})` : ""}
                      </button>
                      {item.track_stock && (
                        <div className="flex items-center gap-1">
                          <button data-testid={`pm-item-stock-minus-${item.id}`} onClick={() => adjustStock(item, -1)} disabled={busy} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#f1f5f9" }}><Minus size={14} /></button>
                          <span data-testid={`pm-item-stock-qty-${item.id}`} className="text-sm font-extrabold w-7 text-center" style={{ color: (item.available_qty ?? 0) <= 0 ? "#dc2626" : "#1f2937" }}>{item.available_qty ?? item.stock_qty ?? 0}</span>
                          <button data-testid={`pm-item-stock-plus-${item.id}`} onClick={() => adjustStock(item, 1)} disabled={busy} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "#f1f5f9" }}><Plus size={14} /></button>
                        </div>
                      )}
                      <label className="flex items-center gap-1 text-xs px-2 h-8 rounded-lg" style={{ background: "#f8fafc", color: "#64748b" }} title="Suivre la quantité en stock">
                        <input type="checkbox" data-testid={`pm-item-track-${item.id}`} checked={!!item.track_stock} onChange={(e) => setTrack(item, e.target.checked)} />stock
                      </label>
                      <label className="flex items-center gap-1 text-xs px-2 h-8 rounded-lg" style={{ background: "#f8fafc", color: "#64748b" }} title="Afficher au client, sinon masquer">
                        {item.is_active === false ? "Masqué" : "Affiché"}
                        <input type="checkbox" data-testid={`pm-item-active-${item.id}`} checked={item.is_active !== false} onChange={() => toggleItemActive(item)} />
                      </label>
                      <label className="flex items-center gap-1 text-xs px-2 h-8 rounded-lg" style={{ background: "#f8fafc", color: "#64748b" }}>
                        {item.is_available === false ? "Rupture" : "Dispo"}
                        <input type="checkbox" data-testid={`pm-item-toggle-${item.id}`} checked={item.is_available !== false} onChange={() => toggleItem(item)} />
                      </label>
                      <button data-testid={`pm-item-delete-${item.id}`} onClick={() => deleteItem(item)} disabled={busy}
                        className="w-8 h-8 rounded-lg flex items-center justify-center ml-auto" style={{ background: "#fef2f2", color: "#dc2626" }} title="Supprimer le produit">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  );
                })}

                {/* Nouvelle fiche produit — formulaire clair + bouton de validation visible */}
                <div className="mt-4 pt-4 border-t" style={{ borderColor: "#e8eceb" }}>
                  <p className="text-sm font-extrabold mb-3 flex items-center gap-2" style={{ color: GREEN }}><Plus size={15} /> Ajouter un produit</p>
                  {full && <p data-testid={`pm-quota-full-${cat.id}`} className="text-xs font-bold mb-3 px-3 py-2 rounded-lg" style={{ background: "#fef2f2", color: "#dc2626" }}>Quota atteint ({quota} max). Augmentez le quota (côté Founder) ou retirez un produit pour en ajouter un nouveau.</p>}

                  <label className="text-xs font-bold" style={{ color: "#64748b" }}>Nom du produit *</label>
                  <input data-testid={`pm-item-name-${cat.id}`} value={df.name} onChange={(e) => setDraft(cat.id, { name: e.target.value })}
                    placeholder="ex : Coca-Cola 50 cl" className="w-full rounded-xl px-4 h-11 text-sm outline-none mt-1 mb-3" style={field} />

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="text-xs font-bold" style={{ color: "#64748b" }}>Prix (CHF) *</label>
                      <input data-testid={`pm-item-price-${cat.id}`} value={df.price} onChange={(e) => setDraft(cat.id, { price: e.target.value })}
                        placeholder="5.00" inputMode="decimal" className="w-full rounded-xl px-4 h-11 text-sm outline-none mt-1" style={field} />
                    </div>
                    <div>
                      <label className="text-xs font-bold" style={{ color: "#64748b" }}>Contenance</label>
                      <input data-testid={`pm-item-size-${cat.id}`} value={df.size_label} onChange={(e) => setDraft(cat.id, { size_label: e.target.value })}
                        placeholder="ex : 50 cl" className="w-full rounded-xl px-4 h-11 text-sm outline-none mt-1" style={field} />
                    </div>
                  </div>

                  <label className="text-xs font-bold" style={{ color: "#64748b" }}>Photo du produit</label>
                  <div className="mt-1 mb-1"><ImageUpload testid={`pm-photo-${cat.id}`} value={df.image_url} onChange={(url) => setDraft(cat.id, { image_url: url })} /></div>
                  <input data-testid={`pm-item-image-${cat.id}`} value={df.image_url} onChange={(e) => setDraft(cat.id, { image_url: e.target.value })}
                    placeholder="…ou coller une URL d'image" className="w-full rounded-xl px-4 h-10 text-xs outline-none mb-3" style={field} />

                  <label className="text-xs font-bold" style={{ color: "#64748b" }}>Sous-titre</label>
                  <input data-testid={`pm-item-subtitle-${cat.id}`} value={df.subtitle} onChange={(e) => setDraft(cat.id, { subtitle: e.target.value })}
                    placeholder="ex : Boisson gazeuse" className="w-full rounded-xl px-4 h-11 text-sm outline-none mt-1 mb-3" style={field} />

                  <label className="text-xs font-bold" style={{ color: "#64748b" }}>Description (optionnel)</label>
                  <input data-testid={`pm-item-desc-${cat.id}`} value={df.description} onChange={(e) => setDraft(cat.id, { description: e.target.value })}
                    placeholder="Courte description" className="w-full rounded-xl px-4 h-11 text-sm outline-none mt-1 mb-3" style={field} />

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                      <label className="text-xs font-bold" style={{ color: "#64748b" }}>Offerts</label>
                      <input data-testid={`pm-item-offerts-${cat.id}`} value={df.offerts} onChange={(e) => setDraft(cat.id, { offerts: e.target.value })}
                        placeholder="ex : Glaçons" className="w-full rounded-xl px-4 h-11 text-sm outline-none mt-1" style={field} />
                    </div>
                    <div>
                      <label className="text-xs font-bold" style={{ color: "#64748b" }}>Mots-clés clients</label>
                      <input data-testid={`pm-item-keywords-${cat.id}`} value={df.client_keywords} onChange={(e) => setDraft(cat.id, { client_keywords: e.target.value })}
                        placeholder="ex : soda, apéro" className="w-full rounded-xl px-4 h-11 text-sm outline-none mt-1" style={field} />
                    </div>
                  </div>

                  <button data-testid={`pm-add-item-${cat.id}`} onClick={() => addItem(cat.id)} disabled={busy || full || !df.name.trim() || !String(df.price).trim()}
                    className="w-full h-12 rounded-xl flex items-center justify-center gap-2 text-white font-extrabold text-sm transition-opacity"
                    style={{ background: (full || !df.name.trim() || !String(df.price).trim()) ? "#cbd5e1" : GREEN, opacity: busy ? 0.6 : 1 }}>
                    <Plus size={18} /> {busy ? "Ajout en cours…" : "Ajouter le produit"}
                  </button>
                  <p className="text-[11px] text-center mt-2" style={{ color: "#94a3b8" }}>Astuce : une promotion se règle via le bouton « Promo » du produit, une fois ajouté.</p>
                </div>
              </div>
            );
          })}
        </>
      )}
      {optItem && <PartnerOptionsEditor item={optItem} onClose={() => setOptItem(null)} onSave={saveOptions} />}
      {editItem && <PartnerItemEditor item={editItem} categories={data?.categories || []} focusPromo={editFocusPromo} onAssignCategories={(ids) => assignItemCats(editItem.id, ids)} onClose={() => { setEditItem(null); setEditFocusPromo(false); }} onSave={saveItem} />}
    </div>
  );
}

function ForcePassword({ onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (next.length < 8) { toast.error("8 caractères minimum"); return; }
    setBusy(true);
    try { await partnerChangePassword(current, next); toast.success("Mot de passe mis à jour"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="pm-force-password">
      <div className="flex items-center gap-2 mb-2"><KeyRound size={22} color={ORANGE} /><h1 className="text-2xl font-extrabold" style={{ color: GREEN }}>Changez votre mot de passe</h1></div>
      <p className="text-sm mb-6" style={{ color: "#64748b" }}>Première connexion : choisissez un mot de passe personnel pour sécuriser votre espace.</p>
      <input data-testid="pm-fp-current" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="Mot de passe temporaire" className="w-full rounded-xl px-4 h-11 text-sm outline-none mb-2" style={field} />
      <input data-testid="pm-fp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder="Nouveau mot de passe (8+ car.)" className="w-full rounded-xl px-4 h-11 text-sm outline-none mb-4" style={field} />
      <button data-testid="pm-fp-submit" onClick={submit} disabled={busy} className="w-full rounded-xl font-bold text-white h-12" style={{ background: GREEN, opacity: busy ? 0.6 : 1 }}>{busy ? "…" : "Valider"}</button>
    </div>
  );
}
